/**
 * Utility functions for timezone-aware date and time formatting
 */

/**
 * Get the user's timezone name (e.g., "America/New_York", "Europe/London")
 */
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'Local Time';
  }
};

/**
 * Parse a date string or Date object.
 * Date-only strings (YYYY-MM-DD) are parsed as local dates to avoid timezone shifts.
 * Datetime strings without timezone info are treated as UTC.
 */
const parseDate = (date: Date | string): Date => {
  if (date instanceof Date) {
    return date;
  }

  // Date-only string (YYYY-MM-DD): parse as local date to avoid timezone shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // If the string doesn't end with Z or have timezone offset, assume it's UTC
  if (!date.endsWith('Z') && !date.match(/[+-]\d{2}:\d{2}$/)) {
    return new Date(date + (date.includes('T') ? 'Z' : 'T00:00:00Z'));
  }

  return new Date(date);
};

/**
 * Format a date to a datetime-local input string (YYYY-MM-DDThh:mm) in local time
 */
export const formatDateTimeLocal = (date: Date | string): string => {
  const dateObj = parseDate(date);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Format a date/time with timezone info for display
 * Example: "Jan 15, 2026, 3:30 PM EST"
 */
export const formatDateTimeWithZone = (date: Date | string): string => {
  const dateObj = parseDate(date);

  // Get formatted date and time
  const formatted = dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Get timezone abbreviation
  const timezone = dateObj
    .toLocaleTimeString('en-US', {
      timeZoneName: 'short',
    })
    .split(' ')
    .pop();

  return `${formatted} ${timezone}`;
};

/**
 * Format just the date part (no time)
 */
export const formatDate = (date: Date | string | null): string => {
  if (!date) return '';
  const dateObj = parseDate(date);
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a date range (e.g., "Mar 7 – Mar 8, 2026")
 * Uses long month names when `long` option is true.
 */
export const formatDateRange = (
  start: string | null,
  end: string | null,
  options?: { long?: boolean }
): string => {
  if (!start) return '';
  const monthFormat = options?.long ? 'long' : 'short';
  const s = parseDate(start);
  const startStr = s.toLocaleDateString('en-US', { month: monthFormat, day: 'numeric' });
  if (!end || end === start) {
    return s.toLocaleDateString('en-US', { month: monthFormat, day: 'numeric', year: 'numeric' });
  }
  const e = parseDate(end);
  const endStr = e.toLocaleDateString('en-US', {
    month: monthFormat,
    day: 'numeric',
    year: 'numeric',
  });
  return `${startStr} – ${endStr}`;
};
